<template>
  <q-expansion-item
    popup
    :header-class="['runai-expansion-header', 'text-weight-medium']"
    expand-separator
    :model-value="isOpen"
    @update:model-value="onModelChanged"
    :disable="disable"
    :expand-icon-class="['runai-expansion-icon', (disableClosing || hideExpendIcon || !hasContent) && 'display-none']"
    class="runai-expansion-item"
    :label="label"
    @after-show="$emit('after-show', $event)"
    :aid="aidToggle"
    @before-hide="validateForm"
  >
    <template #header>
      <div class="runai-expansion-header-content row">
        <div class="runai-expansion-header-content-left row items-center">
          <slot name="header">
            <div :aid="aidLabel">{{ label }}</div>
          </slot>
        </div>
        <div
          class="flex-1 runai-expansion-header-content-right justify-between text-weight-regular"
          v-if="!isOpen || showSubHeader"
        >
          <div class="flex-1 subheader" :aid="aidSubheader">
            <slot name="subheader">{{ subheader }}</slot>
          </div>
          <q-icon
            v-if="isInvalid"
            class="q-mr-md"
            name="fa-regular fa-circle-exclamation"
            color="red"
            size="18px"
          ></q-icon>
        </div>
      </div>
    </template>

    <template v-if="hasContent">
      <q-separator />
      <slot name="content-header" />
      <q-card class="runai-expansion-content">
        <q-card-section :class="{ 'no-padding': bodyNoPadding }">
          <div class="loading-section" v-if="loading">
            <q-skeleton height="230px" square></q-skeleton>
          </div>
          <template v-else>
            <q-form
              v-if="shouldValidate"
              greedy
              ref="formRef"
              @vue:updated="updateRenderKey"
              @validation-error="updateRenderKey"
            >
              <slot />
            </q-form>
            <slot v-else />
          </template>
        </q-card-section>
      </q-card>
    </template>
  </q-expansion-item>
</template>

<script lang="ts">
import { defineComponent, type PropType, ref, type Ref } from "vue";
import type { QForm, QFormChildComponent, QField } from "quasar";
import { useFormChild } from "quasar";

export default defineComponent({
  emits: ["after-show"],
  props: {
    label: {
      type: String as PropType<string>,
      required: false,
    },
    subheader: {
      type: String as PropType<string>,
      required: false,
    },
    defaultOpened: {
      type: Boolean as PropType<boolean>,
      required: false,
      default: false,
    },
    disable: {
      type: Boolean as PropType<boolean>,
      required: false,
      default: false,
    },
    hideExpendIcon: {
      type: Boolean as PropType<boolean>,
      required: false,
      default: false,
    },
    showSubHeader: {
      type: Boolean as PropType<boolean>,
      required: false,
      default: false,
    },
    disableClosing: {
      type: Boolean as PropType<boolean>,
      required: false,
      default: false,
    },
    disableOpening: {
      type: Boolean as PropType<boolean>,
      required: false,
      default: false,
    },
    sectionInvalid: {
      type: Boolean as PropType<boolean>,
      required: false,
      default: false,
    },
    loading: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    shouldValidate: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    bodyNoPadding: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
  },
  data() {
    return {
      isOpen: this.defaultOpened || this.disableClosing,
      renderKey: 1000,
    };
  },
  setup() {
    const formRef: Ref<QForm | null> = ref(null);

    const validate = async () => {
      return await (formRef.value as QForm)?.validate();
    };

    useFormChild({
      validate,
    });

    return { formRef };
  },
  computed: {
    hasContent(): boolean {
      return !!this.$slots.default;
    },
    aidLabel(): string {
      return `${this.label}-expansion-label`.toLowerCase();
    },
    aidToggle(): string {
      return `${this.label}-expansion-toggle`.toLowerCase();
    },
    aidSubheader(): string {
      return `${this.label}-expansion-subheader`.toLowerCase();
    },
    hasError(): boolean {
      this.renderKey; // DO NOT REMOVE THIS LINE - it's critical to trigger reactivity when new components are dynamically added/removed to the QForm
      return !!(this.$refs.formRef as QForm)?.getValidationComponents().some((c: QFormChildComponent) => {
        return (c as QField).hasError;
      });
    },
    isInvalid(): boolean {
      if (this.shouldValidate) {
        return this.hasError;
      }
      return this.sectionInvalid || false;
    },
  },
  methods: {
    onModelChanged(isOpen: boolean): void {
      if (this.disableOpening) {
        this.isOpen = false;
      } else if (!this.disableClosing && this.hasContent) {
        this.isOpen = isOpen;
      }
    },
    updateRenderKey() {
      this.renderKey++;
    },
    validateForm(): void {
      if (!this.shouldValidate) return;
      (this.$refs.formRef as QForm)?.validate();
    },
  },
});
</script>

<style lang="scss" scoped>
.runai-expansion-item {
  &.q-expansion-item--popup.q-expansion-item--collapsed {
    padding-left: 0;
    padding-right: 0;
  }

  .runai-expansion-header {
    .runai-expansion-header-content {
      width: 100%;

      .subheader {
        line-height: 16px;
      }

      .runai-expansion-header-content-left {
        width: 370px;
      }
      .runai-expansion-header-content-right {
        display: flex;
        align-items: center;
        color: $black-54;
      }
    }
  }
}
</style>

<style lang="scss">
.runai-expansion-item {
  > :first-child {
    box-shadow: 0 1px 4px 0 #00000040;
  }
  .runai-expansion-header {
    background-color: $white;

    .runai-expansion-icon .q-icon {
      @extend .text-body2;
    }
  }
  .loading-section {
    width: 100%;
  }
}
</style>
